import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/authContext';
import { confirmAndLogout } from '../utils/session';
import AuthImage from "../components/AuthImage";

const formatFecha = (s) => {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d)) return s;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatFechaHora = (s) => {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d)) return s;
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const today = () => new Date().toISOString().slice(0, 10);

const CATEGORIAS_GASTO = ['menaje', 'comida', 'limpieza', 'varios', 'aparcamiento'];
const TIPOS_LUGAR_GASTO = ['piso', 'reuniones'];

const emptyGastoForm = (user = '') => ({
  user_supervisora: user,
  categoria: 'menaje',
  tipo_lugar: 'piso',
  id_piso: '',
  abreviatura_piso: '',
  sinPiso: false,
  importe: '',
  fecha_gasto: today(),
  notas: '',
  foto: null,
});

const SUP_COLORS = [
  { border: '#3b82f6', headerBg: 'linear-gradient(100deg, #eff6ff 0%, #f8fbff 60%, #fff 100%)', accent: '#1d4ed8', lightBg: '#eff6ff', tag: '#dbeafe' },
  { border: '#10b981', headerBg: 'linear-gradient(100deg, #ecfdf5 0%, #f5fdf9 60%, #fff 100%)', accent: '#047857', lightBg: '#ecfdf5', tag: '#d1fae5' },
  { border: '#8b5cf6', headerBg: 'linear-gradient(100deg, #f5f3ff 0%, #faf8ff 60%, #fff 100%)', accent: '#6d28d9', lightBg: '#f5f3ff', tag: '#ede9fe' },
  { border: '#f59e0b', headerBg: 'linear-gradient(100deg, #fffbeb 0%, #fdfaf0 60%, #fff 100%)', accent: '#b45309', lightBg: '#fffbeb', tag: '#fef3c7' },
  { border: '#ef4444', headerBg: 'linear-gradient(100deg, #fef2f2 0%, #fdf7f7 60%, #fff 100%)', accent: '#b91c1c', lightBg: '#fef2f2', tag: '#fee2e2' },
  { border: '#06b6d4', headerBg: 'linear-gradient(100deg, #ecfeff 0%, #f5fdff 60%, #fff 100%)', accent: '#0e7490', lightBg: '#ecfeff', tag: '#cffafe' },
  { border: '#ec4899', headerBg: 'linear-gradient(100deg, #fdf2f8 0%, #fef7fb 60%, #fff 100%)', accent: '#be185d', lightBg: '#fdf2f8', tag: '#fce7f3' },
  { border: '#84cc16', headerBg: 'linear-gradient(100deg, #f7fee7 0%, #fafdf0 60%, #fff 100%)', accent: '#4d7c0f', lightBg: '#f7fee7', tag: '#ecfccb' },
];

const Contabilidad = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const rolHeader = { 'X-Rol': 'contabilidad' };

  const [supervisoras, setSupervisoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal nuevo préstamo
  const [showPrestamo, setShowPrestamo] = useState(false);
  const [prestamoForm, setPrestamoForm] = useState({ user_supervisora: '', importe: '', notas: '' });
  const [prestamoError, setPrestamoError] = useState('');
  const [prestamoSubmitting, setPrestamoSubmitting] = useState(false);

  // Editar gasto
  const [editGasto, setEditGasto] = useState(null);
  const [editGastoErr, setEditGastoErr] = useState('');
  const [editGastoSubmitting, setEditGastoSubmitting] = useState(false);

  // Añadir gasto (por supervisora)
  const [showGasto, setShowGasto] = useState(false);
  const [gastoForm, setGastoForm] = useState(emptyGastoForm());
  const [gastoPisos, setGastoPisos] = useState([]);
  const [gastoError, setGastoError] = useState('');
  const [gastoSubmitting, setGastoSubmitting] = useState(false);

  // Editar préstamo
  const [editPrestamo, setEditPrestamo] = useState(null);
  const [editPrestamoErr, setEditPrestamoErr] = useState('');
  const [editPrestamoSubmitting, setEditPrestamoSubmitting] = useState(false);

  // Tipo modal préstamo: 'prestamo' | 'devolucion'
  const [tipoModal, setTipoModal] = useState('prestamo');

  // Lista de todas las supervisoras del sistema
  const [listaUsersSupervisoras, setListaUsersSupervisoras] = useState([]);

  // Foto ampliada
  const [fotoAmpliada, setFotoAmpliada] = useState(null);

  // APU
  const [showAPU, setShowAPU] = useState(false);
  const [apuFechaDesde, setApuFechaDesde] = useState('');
  const [apuFechaHasta, setApuFechaHasta] = useState('');
  const [apuError, setApuError] = useState('');

  // Expandidos
  const [expandedSups, setExpandedSups] = useState({});
  const [expandedBolsas, setExpandedBolsas] = useState({});

  useEffect(() => {
    fetchResumen();
    fetchListaSupervisoras();
  }, []);

  const fetchListaSupervisoras = async () => {
    try {
      const r = await axios.get('/api/usuarios/');
      const lista = Array.isArray(r.data) ? r.data : (r.data.data || []);
      setListaUsersSupervisoras(
        lista.filter((u) => u.tipo === 'supervisora').map((u) => u.user).sort((a, b) => a.localeCompare(b, 'es'))
      );
    } catch (e) {}
  };

  const fetchResumen = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get('/api/prestamos/prestamos/resumen', { headers: rolHeader });
      setSupervisoras(r.data.supervisoras || []);
    } catch (e) {
      setError('Error cargando datos: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  };

  const descargarExcelBolsa = (user, n_bolsa, gastos, fecha_liquidacion) => {
    const TIPO_LABEL_MAP = { piso: 'Piso', reuniones: 'Reuniones' };
    const filas = gastos.map(g => ({
      ID: g.id,
      Supervisora: user,
      'Nº Bolsa': n_bolsa,
      'Tipo lugar': g.categoria === 'aparcamiento' ? 'Aparcamiento' : (TIPO_LABEL_MAP[g.tipo_lugar] || g.tipo_lugar || ''),
      Piso: g.abreviatura_piso || '',
      Categoría: g.categoria || '',
      'Importe (€)': parseFloat(g.importe).toFixed(2),
      'Fecha gasto': g.fecha_gasto || '',
      'Fecha creación': g.fecha_creacion || '',
      Notas: g.notas || '',
      'Foto ticket': g.foto_path || '',
      'Fecha liquidación': fecha_liquidacion || '',
    }));
    const total = gastos.reduce((acc, g) => acc + parseFloat(g.importe || 0), 0);
    filas.push({ ID: '', Supervisora: '', 'Nº Bolsa': '', 'Tipo lugar': '', Piso: '', Categoría: 'TOTAL', 'Importe (€)': total.toFixed(2), 'Fecha gasto': '', 'Fecha creación': '', Notas: '', 'Foto ticket': '', 'Fecha liquidación': '' });
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Bolsa_${n_bolsa}`);
    const fecha = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
    XLSX.writeFile(wb, `gastos_${user}_bolsa${n_bolsa}_${fecha}.xlsx`);
  };

  const handleLiquidar = async (user, n_bolsa) => {
    if (!window.confirm(`¿Liquidar la bolsa #${n_bolsa} de ${user}? Esta acción no se puede deshacer.`)) return;
    try {
      // Descargar Excel antes de liquidar (mientras los datos aún están en memoria)
      const supData = supervisoras.find(s => s.user_supervisora === user);
      const bolsaData = supData?.bolsas?.find(b => b.n_bolsa === n_bolsa);
      if (bolsaData?.gastos?.length) {
        descargarExcelBolsa(user, n_bolsa, bolsaData.gastos, bolsaData.fecha_liquidacion || new Date().toLocaleString('es-ES'));
      }
      await axios.post('/api/prestamos/gastos/liquidar', { user_supervisora: user, n_bolsa }, { headers: rolHeader });
      await fetchResumen();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al liquidar');
    }
  };

  const handleEliminarGasto = async (gasto) => {
    if (!window.confirm(`❗⚠️¿Eliminar gasto #${gasto.id} de ${parseFloat(gasto.importe).toFixed(2)}€?⚠️❗`)) return;
    if (!window.confirm(`⚠️ ÚLTIMA CONFIRMACIÓN ⚠️\n\nVas a eliminar DEFINITIVAMENTE el gasto #${gasto.id} (${parseFloat(gasto.importe).toFixed(2)}€).\n\n❗❗❗Esta acción NO se puede deshacer.❗❗❗\n\n¿Seguro que quieres continuar?`)) return;
    try {
      await axios.delete(`/api/prestamos/gastos/${gasto.id}`, { headers: rolHeader });
      await fetchResumen();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar gasto');
    }
  };

  const handleEliminarPrestamo = async (id, user) => {
    if (!window.confirm(`¿Eliminar el adelanto #${id} de ${user}?`)) return;
    try {
      await axios.delete(`/api/prestamos/prestamos/${id}`, { headers: rolHeader });
      await fetchResumen();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar adelanto');
    }
  };

  const handleCrearPrestamo = async (e) => {
    e.preventDefault();
    setPrestamoError('');
    if (!prestamoForm.user_supervisora || !prestamoForm.importe) {
      setPrestamoError('Supervisora e importe son obligatorios');
      return;
    }
    const importeAbs = Math.abs(parseFloat(prestamoForm.importe));
    const importeFinal = tipoModal === 'devolucion' ? -importeAbs : importeAbs;
    if (tipoModal === 'devolucion') {
      if (!window.confirm(`¿Registrar devolución de ${importeAbs.toFixed(2)} € para ${prestamoForm.user_supervisora}?`)) return;
    }
    setPrestamoSubmitting(true);
    try {
      await axios.post('/api/prestamos/prestamos', { ...prestamoForm, importe: importeFinal }, { headers: { ...rolHeader, 'Content-Type': 'application/json' } });
      setShowPrestamo(false);
      setPrestamoForm({ user_supervisora: '', importe: '', notas: '' });
      await fetchResumen();
    } catch (e) {
      setPrestamoError(e.response?.data?.error || 'Error al crear adelanto');
    } finally {
      setPrestamoSubmitting(false);
    }
  };

  const handleGuardarEditPrestamo = async (e) => {
    e.preventDefault();
    setEditPrestamoErr('');
    if (!editPrestamo) return;
    setEditPrestamoSubmitting(true);
    try {
      await axios.put(`/api/prestamos/prestamos/${editPrestamo.id}`, {
        importe: editPrestamo.importe,
        notas: editPrestamo.notas || '',
      }, { headers: { ...rolHeader, 'Content-Type': 'application/json' } });
      setEditPrestamo(null);
      await fetchResumen();
    } catch (e) {
      setEditPrestamoErr(e.response?.data?.error || 'Error al guardar');
    } finally {
      setEditPrestamoSubmitting(false);
    }
  };

  const handleGuardarEditGasto = async (e) => {
    e.preventDefault();
    setEditGastoErr('');
    if (!editGasto) return;
    setEditGastoSubmitting(true);
    try {
      const payload = {
        categoria: editGasto.categoria,
        importe: editGasto.importe,
        fecha_gasto: editGasto.fecha_gasto,
        notas: editGasto.notas || '',
        rol: 'contabilidad',
      };
      if (editGasto.categoria !== 'aparcamiento') {
        payload.tipo_lugar = editGasto.tipo_lugar;
        payload.id_piso = editGasto.id_piso || null;
        payload.abreviatura_piso = editGasto.abreviatura_piso || '';
      } else {
        payload.tipo_lugar = null;
        payload.id_piso = null;
        payload.abreviatura_piso = '';
      }
      await axios.put(`/api/prestamos/gastos/${editGasto.id}`, payload, { headers: { ...rolHeader, 'Content-Type': 'application/json' } });
      setEditGasto(null);
      await fetchResumen();
    } catch (e) {
      setEditGastoErr(e.response?.data?.error || 'Error al guardar');
    } finally {
      setEditGastoSubmitting(false);
    }
  };

  const handleAbrirGasto = async (user) => {
    setGastoError('');
    setGastoForm(emptyGastoForm(user));
    setGastoPisos([]);
    setShowGasto(true);
    try {
      const r = await axios.get(`/api/pisoCrearCompras/pisos_supervisora?user=${encodeURIComponent(user)}`);
      setGastoPisos(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setGastoPisos([]);
    }
  };

  const handleCerrarGasto = () => {
    setShowGasto(false);
    setGastoForm(emptyGastoForm());
    setGastoError('');
  };

  const handleTipoLugarChangeGasto = (v) => {
    setGastoForm((f) => ({ ...f, tipo_lugar: v, id_piso: '', abreviatura_piso: '', sinPiso: false }));
  };

  const handlePisoChangeGasto = (e) => {
    const id = e.target.value;
    const piso = gastoPisos.find((p) => String(p.id) === String(id));
    setGastoForm((f) => ({ ...f, id_piso: id, abreviatura_piso: piso ? piso.abreviatura : '' }));
  };

  const handleToggleSinPisoGasto = () => {
    setGastoForm((f) => ({ ...f, sinPiso: !f.sinPiso, id_piso: '', abreviatura_piso: '' }));
  };

  const handleSubmitGasto = async (e) => {
    e.preventDefault();
    setGastoError('');
    if (!gastoForm.user_supervisora) {
      setGastoError('Selecciona una supervisora');
      return;
    }
    if (!gastoForm.importe || parseFloat(gastoForm.importe) <= 0) {
      setGastoError('El importe debe ser mayor que 0');
      return;
    }
    if (!gastoForm.fecha_gasto) {
      setGastoError('La fecha del gasto es obligatoria');
      return;
    }
    if (gastoForm.categoria !== 'aparcamiento' && !gastoForm.tipo_lugar) {
      setGastoError('Selecciona un lugar');
      return;
    }

    setGastoSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('user_supervisora', gastoForm.user_supervisora);
      fd.append('categoria', gastoForm.categoria);
      fd.append('importe', gastoForm.importe);
      fd.append('fecha_gasto', gastoForm.fecha_gasto);
      fd.append('notas', gastoForm.notas || '');
      if (gastoForm.categoria !== 'aparcamiento') {
        fd.append('tipo_lugar', gastoForm.tipo_lugar);
        fd.append('id_piso', gastoForm.id_piso);
        fd.append('abreviatura_piso', gastoForm.abreviatura_piso);
      }
      if (gastoForm.foto) fd.append('foto', gastoForm.foto);

      await axios.post('/api/prestamos/gastos', fd, { headers: { ...rolHeader, 'Content-Type': 'multipart/form-data' } });
      handleCerrarGasto();
      await fetchResumen();
    } catch (e) {
      setGastoError(e.response?.data?.error || 'Error al crear el gasto');
    } finally {
      setGastoSubmitting(false);
    }
  };

  const toggleSup = (user) => setExpandedSups((p) => ({ ...p, [user]: !p[user] }));
  const toggleBolsa = (key) => setExpandedBolsas((p) => ({ ...p, [key]: !p[key] }));

  const handleLogout = () => confirmAndLogout(navigate, '/login');

  const CUENTA_MAP = {
    menaje: '601000002',
    comida: '601000000',
    limpieza: '601000001',
    varios: '601000004',
    aparcamiento: '629000011',
  };

  const fmtApuFecha = (s) => {
    if (!s) return '';
    const iso = s.slice(0, 10); // 'YYYY-MM-DD'
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const handleGenerarAPU = () => {
    setApuError('');
    // Confirmar rango
    if (!apuFechaDesde || !apuFechaHasta) {
      setApuError('Debes seleccionar ambas fechas');
      return;
    }
    if (apuFechaDesde > apuFechaHasta) {
      setApuError('La fecha inicial debe ser menor o igual que la final');
      return;
    }
    const rangoStr = `${apuFechaDesde.split('-').reverse().join('/')} - ${apuFechaHasta.split('-').reverse().join('/')}`;
    if (!window.confirm(`¿Quieres generar APU de las liquidaciones del ${rangoStr}?`)) return;

    const filas = [];
    supervisoras.forEach((sup) => {
      sup.bolsas
        .filter((b) => {
          if (b.estado !== 'liquidada' || !b.fecha_liquidacion) return false;
          const fechaLiq = b.fecha_liquidacion.slice(0, 10);
          return fechaLiq >= apuFechaDesde && fechaLiq <= apuFechaHasta;
        })
        .forEach((bolsa) => {
          let orden = 1;
          let totalBolsa = 0;
          bolsa.gastos.forEach((g) => {
            const imp = parseFloat(g.importe || 0);
            totalBolsa += imp;
            filas.push({
              Diario: 1,
              Fecha: fmtApuFecha(bolsa.fecha_liquidacion),
              Cuenta: CUENTA_MAP[g.categoria] || '',
              'Importe pesetas': '',
              Concepto: `Ticket ${sup.user_supervisora} ${g.categoria}`,
              Documento: '',
              'Importe DEBE en euros': imp.toFixed(2),
              'Importe HABER en euros': '',
              Moneda: 'E',
              Punteo: 0,
              'Tipo de IVA': '',
              'Código de IVA': '',
              Departamento: g.id_piso || '',
              Subdepartamento: '',
              'Archivo de imagen': '',
            });
          });
          // Fila total
          filas.push({
            Diario: 1,
            Fecha: fmtApuFecha(bolsa.fecha_liquidacion),
            Cuenta: '570000000',
            'Importe pesetas': '',
            Concepto: `Gastos ticket ${sup.user_supervisora}`,
            Documento: '',
            'Importe DEBE en euros': '',
            'Importe HABER en euros': totalBolsa.toFixed(2),
            Moneda: 'E',
            Punteo: 0,
            'Tipo de IVA': '',
            'Código de IVA': '',
            Departamento: '',
            Subdepartamento: '',
            'Archivo de imagen': '',
          });
        });
    });

    if (filas.length === 0) {
      alert('No hay bolsas liquidadas en ese rango de fechas.');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(filas, {
      header: ['Diario','Fecha','Asiento','Orden','Cuenta','Importe pesetas','Concepto','Documento','Importe DEBE en euros','Importe HABER en euros','Moneda','Punteo','Tipo de IVA','Código de IVA','Departamento','Subdepartamento','Archivo de imagen'],
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'APU');
    const fechaHoy = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
    XLSX.writeFile(wb, `APU_${apuFechaDesde}_${apuFechaHasta}_${fechaHoy}.xlsx`);
    setShowAPU(false);
  };

  return (
    <div style={st.page}>
      {/* Header */}
      <div style={st.header}>
        <h2 style={st.titulo}>Panel Contabilidad</h2>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => { setTipoModal('prestamo'); setShowPrestamo(true); }} style={st.btnPrestamo}>+ Adelanto</button>
          <button onClick={() => { setTipoModal('devolucion'); setShowPrestamo(true); }} style={st.btnDevolucion}>💸 Devolución</button>
          <button onClick={() => { setApuError(''); setShowAPU(true); }} style={st.btnAPU}>📊 Generar APU</button>
          <button onClick={handleLogout} style={st.btnLogout}>Cerrar sesión</button>
        </div>
      </div>

      {loading && <p style={st.msg}>Cargando...</p>}
      {error && <p style={{ ...st.msg, color: '#ef4444' }}>{error}</p>}

      {!loading && !error && supervisoras.length === 0 && (
        <p style={st.msg}>No hay datos aún.</p>
      )}

      {supervisoras.map((sup, idx) => {
        const isExpanded = expandedSups[sup.user_supervisora];
        const saldoColor = sup.saldo >= 0 ? '#059669' : '#ef4444';
        const saldoBg = sup.saldo >= 0 ? '#ecfdf5' : '#fef2f2';
        const pal = SUP_COLORS[idx % SUP_COLORS.length];

        return (
          <div key={sup.user_supervisora} style={{ ...st.supCard, borderLeft: `4px solid ${pal.border}` }}>
            {/* Cabecera supervisora */}
            <div style={{ ...st.supHeader, background: pal.headerBg }} onClick={() => toggleSup(sup.user_supervisora)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: pal.border, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 16, flexShrink: 0 }}>
                  {sup.user_supervisora.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ ...st.supNombre, color: pal.accent }}>{sup.user_supervisora}</div>
                  <div style={st.supSubtitle}>
                    Prestado: <strong>{sup.total_prestado.toFixed(2)}€</strong>
                    {' · '}Gastado (liq.): <strong>{sup.total_gastado_liquidado.toFixed(2)}€</strong>
                    {' · '}Pendiente: <strong>{sup.total_pendiente.toFixed(2)}€</strong>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <div style={{ background: saldoBg, color: saldoColor, borderRadius: 10, padding: '4px 12px', fontWeight: 800, fontSize: 16, border: `1px solid ${sup.saldo >= 0 ? '#a7f3d0' : '#fecaca'}` }}>
                  {sup.saldo >= 0 ? '+' : ''}{sup.saldo.toFixed(2)} €
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleAbrirGasto(sup.user_supervisora); }}
                    style={st.btnAddGasto}
                  >
                    ➕ Gasto
                  </button>
                  <div style={st.chevron}>{isExpanded ? '▲' : '▼'}</div>
                </div>
              </div>
            </div>

            {isExpanded && (
              <div style={st.supBody}>

                {/* Préstamos (positivos) */}
                {(() => {
                  const prestamosPos = sup.prestamos.filter((p) => parseFloat(p.importe) > 0);
                  return (
                    <div style={st.seccion}>
                      <div style={{ ...st.seccionTitulo, color: pal.accent, borderBottom: `2px solid ${pal.tag}`, paddingBottom: 4 }}>Adelantos</div>
                      {prestamosPos.length === 0 ? (
                        <p style={st.empty}>Sin adelantos.</p>
                      ) : (
                        prestamosPos.map((p) => (
                          <div key={p.id} style={st.prestamoRow}>
                            <div>
                              <span style={st.prestamoImporte}>{parseFloat(p.importe).toFixed(2)} €</span>
                              <span style={st.prestamoFecha}> · {formatFechaHora(p.fecha)}</span>
                              {p.notas && <span style={st.prestamoNotas}> · {p.notas}</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => setEditPrestamo({ ...p })} style={st.btnEditP}>✏️ Editar</button>
                              <button onClick={() => handleEliminarPrestamo(p.id, sup.user_supervisora)} style={st.btnDelP}>⚠️ Eliminar</button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  );
                })()}

                {/* Devoluciones (negativos) */}
                {(() => {
                  const devoluciones = sup.prestamos.filter((p) => parseFloat(p.importe) < 0);
                  return (
                    <div style={st.seccion}>
                      <div style={{ ...st.seccionTitulo, color: '#d97706', borderBottom: '2px solid #fef3c7', paddingBottom: 4 }}>Devoluciones</div>
                      {devoluciones.length === 0 ? (
                        <p style={st.empty}>Sin devoluciones.</p>
                      ) : (
                        devoluciones.map((p) => (
                          <div key={p.id} style={st.prestamoRow}>
                            <div>
                              <span style={{ ...st.prestamoImporte, color: '#ef4444' }}>{parseFloat(p.importe).toFixed(2)} €</span>
                              <span style={st.prestamoFecha}> · {formatFechaHora(p.fecha)}</span>
                              {p.notas && <span style={st.prestamoNotas}> · {p.notas}</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => setEditPrestamo({ ...p })} style={st.btnEditP}>✏️ Editar</button>
                              <button onClick={() => handleEliminarPrestamo(p.id, sup.user_supervisora)} style={st.btnDelP}>⚠️ Eliminar</button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  );
                })()}

                {/* Bolsas */}
                <div style={st.seccion}>
                  <div style={{ ...st.seccionTitulo, color: pal.accent, borderBottom: `2px solid ${pal.tag}`, paddingBottom: 4 }}>Bolsas de gastos</div>
                  {sup.bolsas.length === 0 ? (
                    <p style={st.empty}>Sin gastos registrados.</p>
                  ) : (
                    sup.bolsas.map((bolsa) => {
                      const bolsaKey = `${sup.user_supervisora}_${bolsa.n_bolsa}`;
                      const bolsaExp = expandedBolsas[bolsaKey];
                      return (
                        <div key={bolsa.n_bolsa} style={{ ...st.bolsaCard, borderLeft: `3px solid ${pal.border}` }}>
                          <div style={{ ...st.bolsaCab, background: pal.lightBg }} onClick={() => toggleBolsa(bolsaKey)}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ ...st.bolsaN, color: pal.accent }}>Bolsa #{bolsa.n_bolsa}</span>
                                <span style={{ ...st.badge, background: bolsa.estado === 'liquidada' ? '#10b981' : '#f59e0b' }}>
                                  {bolsa.estado === 'liquidada' ? 'Liquidada' : 'Activa'}
                                </span>
                              </div>
                              {bolsa.estado === 'liquidada' && bolsa.fecha_liquidacion && (
                                <span style={{ fontSize: 11, color: '#6b7280' }}>
                                  Liquidada el {formatFecha(bolsa.fecha_liquidacion)}
                                </span>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <span style={st.bolsaTotal}>{bolsa.total.toFixed(2)} €</span>
                              {bolsa.estado !== 'liquidada' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleLiquidar(sup.user_supervisora, bolsa.n_bolsa); }}
                                  style={st.btnLiquidar}
                                >
                                  Liquidar
                                </button>
                              )}
                              <span style={{ fontSize: 10, color: '#9ca3af' }}>{bolsaExp ? '▲' : '▼'}</span>
                            </div>
                          </div>

                          {bolsaExp && (
                            <div style={st.bolsaDetalle}>
                              {bolsa.gastos.map((g) => (
                                <GastoContabilidad
                                  key={g.id}
                                  gasto={g}
                                  onEliminar={handleEliminarGasto}
                                  onEditar={(g) => setEditGasto(g)}
                                  onVerFoto={setFotoAmpliada}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Modal nuevo préstamo / devolución */}
      {showPrestamo && (
        <div style={st.overlay} onClick={(e) => { if (e.target === e.currentTarget) setShowPrestamo(false); }}>
          <div style={st.modal}>
            <h3 style={st.modalTitulo}>{tipoModal === 'devolucion' ? '💸 Nueva devolución' : '+ Nuevo adelanto'}</h3>
            <form onSubmit={handleCrearPrestamo} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={st.label}>Supervisora *</label>
              <select
                style={st.input}
                value={prestamoForm.user_supervisora}
                onChange={(e) => setPrestamoForm((f) => ({ ...f, user_supervisora: e.target.value }))}
                required
              >
                <option value="">-- Seleccionar supervisora --</option>
                {listaUsersSupervisoras.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>

              <label style={st.label}>Importe (€) *</label>
              <input
                style={st.input}
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={prestamoForm.importe}
                onChange={(e) => setPrestamoForm((f) => ({ ...f, importe: e.target.value }))}
                required
              />


              <label style={st.label}>Notas</label>
              <textarea
                style={{ ...st.input, height: 60, resize: 'vertical' }}
                placeholder="Observaciones (opcional)"
                value={prestamoForm.notas}
                onChange={(e) => setPrestamoForm((f) => ({ ...f, notas: e.target.value }))}
              />

              {prestamoError && <p style={{ color: '#ef4444', fontSize: 13, margin: 0 }}>{prestamoError}</p>}

              <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                <button type="submit" style={tipoModal === 'devolucion' ? st.btnDevolucionSubmit : st.btnGuardar} disabled={prestamoSubmitting}>
                  {prestamoSubmitting ? 'Guardando...' : tipoModal === 'devolucion' ? '💸 Crear devolución' : '➕ Crear adelanto'}
                </button>
                <button type="button" onClick={() => setShowPrestamo(false)} style={st.btnCancelar}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal editar préstamo */}
      {editPrestamo && (
        <div style={st.overlay} onClick={(e) => { if (e.target === e.currentTarget) setEditPrestamo(null); }}>
          <div style={st.modal}>
            <h3 style={st.modalTitulo}>✏️ Editar adelanto #{editPrestamo.id}</h3>
            <form onSubmit={handleGuardarEditPrestamo} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={st.label}>Importe (€) *</label>
              <input
                style={st.input}
                type="number"
                step="0.01"
                placeholder="0.00"
                value={editPrestamo.importe}
                onChange={(e) => setEditPrestamo((p) => ({ ...p, importe: e.target.value }))}
                required
              />
              <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Usa importe negativo para devoluciones.</p>
              <label style={st.label}>Notas</label>
              <textarea
                style={{ ...st.input, height: 60, resize: 'vertical' }}
                value={editPrestamo.notas || ''}
                onChange={(e) => setEditPrestamo((p) => ({ ...p, notas: e.target.value }))}
              />
              {editPrestamoErr && <p style={{ color: '#ef4444', fontSize: 13, margin: 0 }}>{editPrestamoErr}</p>}
              <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                <button type="submit" style={st.btnGuardar} disabled={editPrestamoSubmitting}>
                  {editPrestamoSubmitting ? 'Guardando...' : '💾 Guardar'}
                </button>
                <button type="button" onClick={() => setEditPrestamo(null)} style={st.btnCancelar}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal editar gasto */}
      {editGasto && (
        <div style={st.overlay} onClick={(e) => { if (e.target === e.currentTarget) setEditGasto(null); }}>
          <div style={st.modal}>
            <h3 style={st.modalTitulo}>✏️ Editar gasto #{editGasto.id}</h3>
            <form onSubmit={handleGuardarEditGasto} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={st.label}>Categoría</label>
              <select style={st.input} value={editGasto.categoria} onChange={(e) => setEditGasto((g) => ({ ...g, categoria: e.target.value, tipo_lugar: e.target.value === 'aparcamiento' ? null : (g.tipo_lugar || 'piso'), id_piso: e.target.value === 'aparcamiento' ? null : g.id_piso, abreviatura_piso: e.target.value === 'aparcamiento' ? '' : g.abreviatura_piso }))}>
                <option value="menaje">Menaje</option>
                <option value="comida">Comida</option>
                <option value="limpieza">Limpieza</option>
                <option value="varios">Varios</option>
                <option value="aparcamiento">Aparcamiento</option>
              </select>
              {editGasto.categoria !== 'aparcamiento' && (
                <>
                  <label style={st.label}>Tipo lugar</label>
                  <select style={st.input} value={editGasto.tipo_lugar || 'piso'} onChange={(e) => setEditGasto((g) => ({ ...g, tipo_lugar: e.target.value }))}>
                    <option value="piso">Piso</option>
                    <option value="reuniones">Reuniones</option>
                  </select>
                </>
              )}
              {editGasto.categoria !== 'aparcamiento' && (
                <>
                  <label style={st.label}>Abreviatura piso</label>
                  <input style={st.input} value={editGasto.abreviatura_piso || ''} onChange={(e) => setEditGasto((g) => ({ ...g, abreviatura_piso: e.target.value }))} />
                </>
              )}
              <label style={st.label}>Importe (€) *</label>
              <input style={st.input} type="number" step="0.01" min="0.01" value={editGasto.importe} onChange={(e) => setEditGasto((g) => ({ ...g, importe: e.target.value }))} required />
              <label style={st.label}>Fecha del gasto *</label>
              <input style={st.input} type="date" value={editGasto.fecha_gasto} onChange={(e) => setEditGasto((g) => ({ ...g, fecha_gasto: e.target.value }))} required />
              <label style={st.label}>Notas</label>
              <textarea style={{ ...st.input, height: 60, resize: 'vertical' }} value={editGasto.notas || ''} onChange={(e) => setEditGasto((g) => ({ ...g, notas: e.target.value }))} />
              {editGastoErr && <p style={{ color: '#ef4444', fontSize: 13, margin: 0 }}>{editGastoErr}</p>}
              <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                <button type="submit" style={st.btnGuardar} disabled={editGastoSubmitting}>{editGastoSubmitting ? 'Guardando...' : '💾 Guardar'}</button>
                <button type="button" onClick={() => setEditGasto(null)} style={st.btnCancelar}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal añadir gasto (por supervisora) */}
      {showGasto && (
        <div style={st.overlay} onClick={(e) => { if (e.target === e.currentTarget) handleCerrarGasto(); }}>
          <div style={st.modal}>
            <h3 style={st.modalTitulo}>➕ Nuevo gasto — {gastoForm.user_supervisora}</h3>
            <form onSubmit={handleSubmitGasto} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

              <label style={st.label}>Categoría *</label>
              <div style={st.chipGroup}>
                {CATEGORIAS_GASTO.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setGastoForm((f) => ({ ...f, categoria: c, tipo_lugar: c === 'aparcamiento' ? '' : (f.tipo_lugar || 'piso'), id_piso: '', abreviatura_piso: '', sinPiso: false }))}
                    style={{ ...st.chip, ...(gastoForm.categoria === c ? { border: `1.5px solid ${CAT_COLORS[c].border}`, background: CAT_COLORS[c].bg, color: CAT_COLORS[c].text, fontWeight: 700 } : {}) }}
                  >
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </button>
                ))}
              </div>

              {gastoForm.categoria !== 'aparcamiento' && (
                <>
                  <label style={st.label}>Lugar *</label>
                  <div style={st.chipGroup}>
                    {TIPOS_LUGAR_GASTO.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => handleTipoLugarChangeGasto(t)}
                        style={{ ...st.chip, ...(gastoForm.tipo_lugar === t ? { border: `1.5px solid ${TIPO_COLORS[t].border}`, background: TIPO_COLORS[t].bg, color: TIPO_COLORS[t].text, fontWeight: 700 } : {}) }}
                      >
                        {TIPO_LABEL[t]}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {gastoForm.categoria !== 'aparcamiento' && (gastoForm.tipo_lugar === 'piso' || gastoForm.tipo_lugar === 'reuniones') && (
                <>
                  <label style={st.label}>Piso</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select style={{ ...st.input, flex: 1 }} value={gastoForm.id_piso} onChange={handlePisoChangeGasto} disabled={gastoForm.sinPiso}>
                      <option value="">-- Seleccionar piso --</option>
                      {gastoPisos.map((p) => (
                        <option key={p.id} value={p.id}>{p.abreviatura}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleToggleSinPisoGasto}
                      style={{ ...st.chip, whiteSpace: 'nowrap', ...(gastoForm.sinPiso ? { border: '1.5px solid #6b7280', background: '#374151', color: '#fff', fontWeight: 700 } : {}) }}
                    >
                      {gastoForm.sinPiso ? '✅ Sin piso' : '📍 Sin piso'}
                    </button>
                  </div>
                </>
              )}

              <label style={st.label}>Importe (€) *</label>
              <input
                style={st.input}
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={gastoForm.importe}
                onChange={(e) => setGastoForm((f) => ({ ...f, importe: e.target.value }))}
                required
              />

              <label style={st.label}>Fecha del gasto *</label>
              <input
                style={st.input}
                type="date"
                value={gastoForm.fecha_gasto}
                onChange={(e) => setGastoForm((f) => ({ ...f, fecha_gasto: e.target.value }))}
                required
              />

              <label style={st.label}>Notas</label>
              <textarea
                style={{ ...st.input, height: 60, resize: 'vertical' }}
                placeholder="Observaciones (opcional)"
                value={gastoForm.notas}
                onChange={(e) => setGastoForm((f) => ({ ...f, notas: e.target.value }))}
              />

              <label style={st.label}>Foto del ticket</label>
              <input
                style={st.input}
                type="file"
                accept="image/*"
                onChange={(e) => setGastoForm((f) => ({ ...f, foto: e.target.files[0] || null }))}
              />

              {gastoError && <p style={{ color: '#ef4444', fontSize: 13, margin: 0 }}>{gastoError}</p>}

              <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                <button type="submit" style={st.btnGuardar} disabled={gastoSubmitting}>
                  {gastoSubmitting ? 'Guardando...' : '➕ Crear gasto'}
                </button>
                <button type="button" onClick={handleCerrarGasto} style={st.btnCancelar}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Generar APU */}
      {showAPU && (
        <div style={st.overlay} onClick={(e) => { if (e.target === e.currentTarget) setShowAPU(false); }}>
          <div style={st.modal}>
            <h3 style={st.modalTitulo}>📊 Generar APU</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={st.label}>Fecha desde *</label>
              <input style={st.input} type="date" value={apuFechaDesde} onChange={(e) => setApuFechaDesde(e.target.value)} />
              <label style={st.label}>Fecha hasta *</label>
              <input style={st.input} type="date" value={apuFechaHasta} onChange={(e) => setApuFechaHasta(e.target.value)} />
              {apuError && <p style={{ color: '#ef4444', fontSize: 13, margin: 0 }}>{apuError}</p>}
              <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                <button onClick={handleGenerarAPU} style={st.btnGuardar}>📥 Generar Excel</button>
                <button onClick={() => setShowAPU(false)} style={st.btnCancelar}>Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {fotoAmpliada && (
        <div style={st.overlay} onClick={() => setFotoAmpliada(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, maxWidth: '95vw' }}>
            <AuthImage src={fotoAmpliada} alt="Ticket" style={{ maxWidth: '95vw', maxHeight: '80vh', borderRadius: 10, objectFit: 'contain' }} />
            <button onClick={() => setFotoAmpliada(null)} style={st.btnCancelar}>Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
};

const TIPO_LABEL = { piso: 'Piso', reuniones: 'Reuniones' };

const CAT_COLORS = {
  menaje:       { bg: '#eff6ff', border: '#3b82f6', text: '#1d4ed8' },
  comida:       { bg: '#fff7ed', border: '#f97316', text: '#c2410c' },
  limpieza:     { bg: '#ecfdf5', border: '#10b981', text: '#065f46' },
  varios:       { bg: '#f5f3ff', border: '#8b5cf6', text: '#5b21b6' },
  aparcamiento: { bg: '#fffbeb', border: '#f59e0b', text: '#92400e' },
};

const TIPO_COLORS = {
  piso:      { bg: '#eef2ff', border: '#6366f1', text: '#3730a3' },
  reuniones: { bg: '#fdf2f8', border: '#ec4899', text: '#9d174d' },
};

const GastoContabilidad = ({ gasto, onEliminar, onEditar, onVerFoto }) => {
  const formatFechaHora = (s) => {
    if (!s) return '';
    const d = new Date(s.replace(' ', 'T'));
    if (isNaN(d)) return s;
    return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };
  const formatFecha = (s) => {
    if (!s) return '';
    const d = new Date(s);
    if (isNaN(d)) return s;
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  return (
    <div style={{ ...gc.row, borderLeft: `4px solid ${(CAT_COLORS[gasto.categoria] || CAT_COLORS.varios).border}` }}>
      <div style={gc.left}>
        <span style={{ ...gc.dot, background: gasto.estado === 'liquidado' ? '#10b981' : '#f59e0b' }} />
        <div>
          <div style={{ ...gc.titulo, color: gasto.categoria === 'aparcamiento' ? CAT_COLORS.aparcamiento.text : ((TIPO_COLORS[gasto.tipo_lugar] || {}).text || '#111827') }}>
            #{gasto.id} · {gasto.categoria === 'aparcamiento' ? 'Aparcamiento' : (TIPO_LABEL[gasto.tipo_lugar] || gasto.tipo_lugar || '')}{gasto.abreviatura_piso ? ` (${gasto.abreviatura_piso})` : ''}
          </div>
          <div style={gc.sub}>
            <span style={{ display: 'inline-block', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700, background: (CAT_COLORS[gasto.categoria] || CAT_COLORS.varios).bg, color: (CAT_COLORS[gasto.categoria] || CAT_COLORS.varios).text, border: `1px solid ${(CAT_COLORS[gasto.categoria] || CAT_COLORS.varios).border}` }}>{gasto.categoria}</span>
            {' · Fecha gasto: '}{formatFecha(gasto.fecha_gasto)}
          </div>
          <div style={gc.sub}>Añadido: {formatFechaHora(gasto.fecha_creacion)}</div>
          {gasto.notas && <div style={gc.sub}>Notas: {gasto.notas}</div>}
          {gasto.foto_path && (
            <AuthImage
              src={`/api/tickets_gastos/${encodeURIComponent(gasto.foto_path)}`}
              alt="Ticket"
              style={{ maxWidth: 120, maxHeight: 80, borderRadius: 6, objectFit: 'contain', cursor: 'pointer', marginTop: 4, border: '1px solid #e5e7eb' }}
              onClick={() => onVerFoto(`/api/tickets_gastos/${encodeURIComponent(gasto.foto_path)}`)}
            />
          )}
        </div>
      </div>
      <div style={gc.right}>
        <span style={gc.importe}>{parseFloat(gasto.importe).toFixed(2)} €</span>
        <button onClick={() => onEditar(gasto)} style={gc.btnEdit}>✏️ Editar</button>
        <button onClick={() => onEliminar(gasto)} style={gc.btnDel}>⚠️ Eliminar</button>
      </div>
    </div>
  );
};

const st = {
  page: { maxWidth: 700, margin: '0 auto', padding: '0 0 80px', fontFamily: "'Segoe UI', sans-serif", background: 'linear-gradient(160deg, #f0f4ff 0%, #f9fafb 50%, #f0fdf4 100%)', minHeight: '100vh' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 12px', background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 40, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', flexWrap: 'wrap', gap: 8 },
  titulo: { margin: 0, fontSize: 18, fontWeight: 800, color: '#1e3a8a' },
  btnPrestamo: { background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer', boxShadow: '0 2px 6px rgba(16,185,129,0.3)' },
  btnLogout: { background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  msg: { textAlign: 'center', color: '#6b7280', marginTop: 40 },
  supCard: { margin: '14px 12px 0', background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 4px 18px rgba(0,0,0,0.07)', transition: 'box-shadow 0.2s' },
  supHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px', cursor: 'pointer', userSelect: 'none' },
  supNombre: { fontWeight: 800, fontSize: 16, color: '#111827' },
  supSubtitle: { fontSize: 12, color: '#6b7280', marginTop: 3 },
  saldo: { fontWeight: 800, fontSize: 18 },
  chevron: { fontSize: 11, color: '#9ca3af', textAlign: 'right', marginTop: 2 },
  supBody: { borderTop: '1px solid #f3f4f6', padding: '14px 16px' },
  seccion: { marginBottom: 16 },
  seccionTitulo: { fontWeight: 700, fontSize: 12, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.8 },
  empty: { fontSize: 13, color: '#9ca3af', margin: 0 },
  prestamoRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f3f4f6' },
  prestamoImporte: { fontWeight: 700, fontSize: 14, color: '#10b981' },
  prestamoFecha: { fontSize: 12, color: '#6b7280' },
  prestamoNotas: { fontSize: 12, color: '#6b7280' },
  bolsaCard: { border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  bolsaCab: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', cursor: 'pointer' },
  bolsaN: { fontWeight: 700, fontSize: 14 },
  badge: { color: '#fff', borderRadius: 20, padding: '3px 11px', fontSize: 11, fontWeight: 700 },
  bolsaTotal: { fontWeight: 800, fontSize: 14, color: '#ef4444' },
  btnLiquidar: { background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 13px', fontSize: 12, cursor: 'pointer', fontWeight: 700, boxShadow: '0 2px 5px rgba(16,185,129,0.25)' },
  bolsaDetalle: { background: '#fafafa' },
  btnDel: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13 },
  btnDevolucion: { background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  btnDevolucionSubmit: { flex: 1, background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8, padding: 11, fontWeight: 700, fontSize: 15, cursor: 'pointer' },
  btnEditP: { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  btnDelP: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  btnAPU: { background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  btnAddGasto: { background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe', borderRadius: 7, padding: '5px 11px', fontSize: 12, cursor: 'pointer', fontWeight: 700 },
  chipGroup: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  chip: { border: '1.5px solid #d1d5db', borderRadius: 20, padding: '5px 14px', fontSize: 13, cursor: 'pointer', background: '#fff', fontWeight: 500 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: '#fff', borderRadius: 14, padding: '20px 18px', width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto' },
  modalTitulo: { margin: '0 0 14px', fontSize: 17, fontWeight: 700, color: '#1e3a8a' },
  label: { fontWeight: 600, fontSize: 13, color: '#374151' },
  input: { border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 12px', fontSize: 15, outline: 'none', width: '100%', boxSizing: 'border-box' },
  btnGuardar: { flex: 1, background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: 11, fontWeight: 700, fontSize: 15, cursor: 'pointer' },
  btnCancelar: { flex: 1, background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: 11, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
};

const gc = {
  row: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid #f3f4f6' },
  left: { display: 'flex', gap: 8, flex: 1 },
  dot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0, marginTop: 5 },
  titulo: { fontWeight: 600, fontSize: 13, color: '#111827' },
  sub: { fontSize: 12, color: '#6b7280', marginTop: 1 },
  right: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, marginLeft: 8 },
  importe: { fontWeight: 700, fontSize: 14, color: '#ef4444' },
  btnEdit: { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  btnDel: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
};

export default Contabilidad;
